import { css } from '@emotion/react';
import {
  type FC, useCallback, useMemo,
} from 'react';
import {
  Accordion, type AccordionData,
} from '~/_shared/baseComponents/accordion';
import { OutlinePanelComponent } from '~/_shared/baseComponents/outlinePanel/outlinePanel.component';
import { OutlinePanelOptions } from '~/_shared/baseComponents/outlinePanel/outlinePanel.constants';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { CONFIG } from '~/_shared/constants/config';
import { useTranslation } from '~/_shared/utils/hooks';
import { isTextEmpty } from '~/_shared/utils/text/text.helpers';
import { type BoundaryGroup } from '~/boundary/boundary.types';
import { type ModalProps } from '~/modal/modalType.enum';
import {
  getTargetNumberOfTerritories,
  isStepExpanded, isStepLocked,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.helpers';
import {
  BoundaryGroupFromAIModalSteps, BuildingBlockSelections, type BuildingBlockSubsetTypes, type GlobalMetricValue, type MetricValue,
  type NumberOfTerritoriesLastUpdateReason, type SalesRepLocationsMap,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import {
  AdditionalOptionsStepComponent, CreateOptimizedTerritoriesAdditionalOption, WMS_MIN_NUMBER_OF_TERRITORIES,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/additionalOptions.component';
import { BuildingBlockStepComponent } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/buildingBlockStep.component';
import { useOptimizedTerritoriesConstraintsValidation } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/constraintsValidation/useOptimizedTerrotiriesContraintsValidation';
import { NameStepComponent } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/nameStep.component';
import { VariablesStepComponent } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/variablesStep.component';
import type { CreateOptimizedTerritoriesConfig } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/createOptimizedTerritories.types';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import type { FilterTreeItemRequest } from '~/store/spreadsheetData/filtering/spreadsheetDataFiltering.helpers';

export type CreateOptimizedTerritoriesProps = Readonly<{
  config: CreateOptimizedTerritoriesConfig;
  currentStep: BoundaryGroupFromAIModalSteps;
  customBoundaryGroups: ReadonlyArray<BoundaryGroup>;
  isGenerateOptimizedTerritoriesLoading: boolean;
  isLoadingSubsetBlocks?: boolean;
  supportedBoundaryGroups: ReadonlyArray<BoundaryGroup>;
  isLoadingDataMetrics: boolean;
  numberOfTerritoriesLoading: boolean;
  numberOfTerritoriesUpdateReason: NumberOfTerritoriesLastUpdateReason;
  markerFilterActive: boolean;
  onChange: (update: Partial<CreateOptimizedTerritoriesConfig>) => void;
  onCloseOptimizedTerritoriesRequestError: () => void;
  onGoToStep: (step: BoundaryGroupFromAIModalSteps) => void;
  onRefreshTerritoriesCount: () => void;
  onSelectSubsetFromMap: () => void;
  onSubmit: () => void;
  optimizedTerritoriesRequestError: string | null;
  minWeight: number;
  maxWeight: number;
  filter: FilterTreeItemRequest | null;
}>;

const itemButtonStyle = css({
  textTransform: 'uppercase',
});

const CreateOptimizedTerritoriesComponent: FC<CreateOptimizedTerritoriesProps> = ({
  config,
  currentStep,
  customBoundaryGroups,
  supportedBoundaryGroups,
  isGenerateOptimizedTerritoriesLoading,
  isLoadingSubsetBlocks,
  isLoadingDataMetrics,
  numberOfTerritoriesUpdateReason,
  markerFilterActive,
  onChange,
  onCloseOptimizedTerritoriesRequestError,
  onGoToStep,
  onRefreshTerritoriesCount,
  numberOfTerritoriesLoading,
  onSelectSubsetFromMap,
  onSubmit,
  optimizedTerritoriesRequestError,
  minWeight,
  maxWeight,
  filter,
}: CreateOptimizedTerritoriesProps) => {
  const [t] = useTranslation();

  const isExpandedPerStep = useMemo(() => isStepExpanded(currentStep), [currentStep]);
  const isLockedPerStep = useMemo(() => isStepLocked(currentStep), [currentStep]);

  const numberOfTerritories = getTargetNumberOfTerritories(config);

  const boundaryGroupsOptions = useMemo(() => supportedBoundaryGroups.map(boundaryGroup => ({
    value: boundaryGroup.id,
    name: boundaryGroup.name,
  })), [supportedBoundaryGroups]);

  const { isLoading: isConstraintsValidationLoading, validationData: constraintsValidationData, areConstraintsValid, generalError: constraintsGeneralError } = useOptimizedTerritoriesConstraintsValidation({
    boundaryGroupId: config.boundaryGroupId,
    optimizationConstraints: config.optimizationConstraints,
    demographicsValues: config.demographicsValues,
    dataMetricsValues: config.dataMetricsValues,
    blockSubset: config.buildingBlockSelection === BuildingBlockSelections.SUBSET ? config.selectedBoundaryIds : undefined,
    filter: config.applyFilterOnData ? filter : undefined,
  });

  const onChangeGroupName = useCallback((value: string) => {
    onChange({ groupName: value });
  }, [onChange]);

  const onChangeBuildingBlocks = useCallback((value: BoundaryGroupId) => {
    onChange({
      boundaryGroupId: value,
      ...(config.boundaryGroupId !== value ? { selectedBoundaryIds: [] } : undefined),
    });
  }, [config.boundaryGroupId, onChange]);

  const onChangeDataMetrics = useCallback((value: MetricValue[]) => {
    onChange({ dataMetricsValues: value });
  }, [onChange]);

  const onChangeGlobalMetrics = useCallback((value: GlobalMetricValue[]) => {
    onChange({ globalMetrics: value });
  }, [onChange]);

  const onChangeDemographics = useCallback((value: MetricValue[]) => {
    onChange({ demographicsValues: value });
  }, [onChange]);

  const onChangeUseNumberOfTerritories = useCallback((flag: boolean) => {
    onChange({
      useNumberOfTerritories: flag,
      ...(flag ? { numberOfTerritories: numberOfTerritories ?? WMS_MIN_NUMBER_OF_TERRITORIES } : { numberOfTerritories: null }),
    });
  }, [numberOfTerritories, onChange]);

  const onChangeApplyFilterOnData = useCallback((flag: boolean) => {
    onChange({ applyFilterOnData: flag });
  }, [onChange]);
  const showApplyDataFilterOption = markerFilterActive && config.dataMetricsValues.some(metric => metric.checked);

  const onChangeRespectSalesLocations = useCallback((flag: boolean) => {
    onChange({
      respectSalesLocations: flag,
      salesRepLocationsMap: null,
      numberOfSalesReps: null,
    });
  }, [onChange]);

  const onChangeSalesRepLocationsMapId = useCallback((map: SalesRepLocationsMap) => {
    onChange({ salesRepLocationsMap: map });
  }, [onChange]);

  const onChangeSalesRepNameColumnId = useCallback((columnId: string) => {
    onChange({ salesRepNameColumnId: columnId });
  }, [onChange]);

  const onChangeRespectPrevTerritories = useCallback((flag: boolean) => {
    onChange({
      respectPrevTerritories: flag,
      previousTerritoriesBoundaryGroupId: null,
      numberOfPreviousTerritories: null,
    });
  }, [onChange]);

  const onChangePreviouslySavedBoundaryGroupId = useCallback((previousTerritoriesBoundaryGroupId: number) => {
    onChange({ previousTerritoriesBoundaryGroupId });
  }, [onChange]);

  const onChangeOptimizationConstraints = useCallback((flag: boolean) => {
    onChange({ optimizationConstraints: flag });
  }, [onChange]);

  const onChangeNumberOfTerritories = useCallback((value: number) => {
    onChange({ numberOfTerritories: value });
  }, [onChange]);

  const onApImportanceChange = useCallback((value: number) => {
    onChange({ anchorPointsImportance: value });
  }, [onChange]);

  const onChangeBuildingBlockSelection = useCallback((value: BuildingBlockSelections) => {
    onChange({ buildingBlockSelection: value });
    if (value === BuildingBlockSelections.ALL && config.selectedBoundaryIds?.length > 0) {
      onChange({ selectedBoundaryIds: [] });
    }
  }, [config.selectedBoundaryIds?.length, onChange]);

  const onChangeBuildingBlockSubsetType = useCallback((value: BuildingBlockSubsetTypes) => {
    onChange({ buildingBlockSubsetType: value });
  }, [onChange]);

  const onChangeOverrideTerritory = useCallback((flag: boolean) => {
    onChange({ overrideTerritory: flag });
  }, [onChange]);

  const onExcludeRemoteAreasChange = useCallback((excludeRemoteAreas: boolean) => {
    onChange({ excludeDisconnectedBlocks: excludeRemoteAreas });
  }, [onChange]);

  const onChangeExportConfig = useCallback((exportConfig: boolean) => {
    onChange({ exportConfig });
  }, [onChange]);

  const isExportConfigEnabled = !!config.exportConfig;
  const options = useMemo(() => {
    return [{ id: 'export-config', label: 'Export Config', value: isExportConfigEnabled }];
  }, [isExportConfigEnabled]);

  const onGoToStepsCallbacks = useMemo(() => ({
    [BoundaryGroupFromAIModalSteps.Name]: () => onGoToStep(BoundaryGroupFromAIModalSteps.Name),
    [BoundaryGroupFromAIModalSteps.BuildingBlock]: () => onGoToStep(BoundaryGroupFromAIModalSteps.BuildingBlock),
    [BoundaryGroupFromAIModalSteps.Variables]: () => onGoToStep(BoundaryGroupFromAIModalSteps.Variables),
    [BoundaryGroupFromAIModalSteps.AdditionalOptions]: () => onGoToStep(BoundaryGroupFromAIModalSteps.AdditionalOptions),
  }), [onGoToStep]);

  const additionalStepInterlocks = [{
    item: CreateOptimizedTerritoriesAdditionalOption.SalesReps,
    lockingItem: CreateOptimizedTerritoriesAdditionalOption.Constraints,
    reason: t('boundaryAI.You cannot use "{{disabled}}" together with "{{enabled}}"', {
      disabled: t('boundaryAI.Respect Sales Reps Locations'),
      enabled: t('boundaryAI.Optimize territories using constraints'),
    }),
  }, {
    item: CreateOptimizedTerritoriesAdditionalOption.SalesReps,
    lockingItem: CreateOptimizedTerritoriesAdditionalOption.NumerOfTerritories,
    reason: t('boundaryAI.You cannot use "{{disabled}}" together with "{{enabled}}"', {
      disabled: t('boundaryAI.Respect Sales Reps Locations'),
      enabled: t('boundaryAI.Number of territories'),
    }),
  }, {
    item: CreateOptimizedTerritoriesAdditionalOption.SalesReps,
    lockingItem: CreateOptimizedTerritoriesAdditionalOption.RespectPreviousTerritories,
    reason: t('boundaryAI.You cannot use "{{disabled}}" together with "{{enabled}}"', {
      disabled: t('boundaryAI.Respect Sales Reps Locations'),
      enabled: t('boundaryAI.Respect previous Territories'),
    }),
  }, {
    item: CreateOptimizedTerritoriesAdditionalOption.RespectPreviousTerritories,
    lockingItem: CreateOptimizedTerritoriesAdditionalOption.SalesReps,
    reason: t('boundaryAI.You cannot use "{{disabled}}" together with "{{enabled}}"', {
      disabled: t('boundaryAI.Respect previous Territories'),
      enabled: t('boundaryAI.Respect Sales Reps Locations'),
    }),
  }, {
    item: CreateOptimizedTerritoriesAdditionalOption.NumerOfTerritories,
    lockingItem: CreateOptimizedTerritoriesAdditionalOption.SalesReps,
    reason: t('boundaryAI.You cannot use "{{disabled}}" together with "{{enabled}}"', {
      disabled: t('boundaryAI.Number of territories'),
      enabled: t('boundaryAI.Respect Sales Reps Locations'),
    }),
  }, {
    item: CreateOptimizedTerritoriesAdditionalOption.NumerOfTerritories,
    lockingItem: CreateOptimizedTerritoriesAdditionalOption.Constraints,
    reason: t('boundaryAI.You cannot use "{{disabled}}" together with "{{enabled}}"', {
      disabled: t('boundaryAI.Number of territories'),
      enabled: t('boundaryAI.Optimize territories using constraints'),
    }),
  }, {
    item: CreateOptimizedTerritoriesAdditionalOption.Constraints,
    lockingItem: CreateOptimizedTerritoriesAdditionalOption.SalesReps,
    reason: t('boundaryAI.You cannot use "{{disabled}}" together with "{{enabled}}"', {
      disabled: t('boundaryAI.Optimize territories using constraints'),
      enabled: t('boundaryAI.Respect Sales Reps Locations'),
    }),
  }, {
    item: CreateOptimizedTerritoriesAdditionalOption.Constraints,
    lockingItem: CreateOptimizedTerritoriesAdditionalOption.NumerOfTerritories,
    reason: t('boundaryAI.You cannot use "{{disabled}}" together with "{{enabled}}"', {
      disabled: t('boundaryAI.Optimize territories using constraints'),
      enabled: t('boundaryAI.Number of territories'),
    }),
  }, {
    item: CreateOptimizedTerritoriesAdditionalOption.RespectPreviousTerritories,
    lockingItem: CreateOptimizedTerritoriesAdditionalOption.Constraints,
    reason: t('boundaryAI.You cannot use "{{disabled}}" together with "{{enabled}}"', {
      disabled: t('boundaryAI.Respect previous Territories'),
      enabled: t('boundaryAI.Optimize territories using constraints'),
    }),
  }];

  const accordionData: AccordionData[] = [{
    header: (
      <Accordion.ItemHeading css={itemButtonStyle}>
        <Accordion.StepNumber />
        <span>{t('boundaryAI.Select Building Block Type')}</span>
      </Accordion.ItemHeading>
    ),
    child: (
      <BuildingBlockStepComponent
        excludeRemoteAreas={config.excludeDisconnectedBlocks}
        onExcludeRemoteAreasChange={onExcludeRemoteAreasChange}
        value={config.boundaryGroupId}
        options={boundaryGroupsOptions}
        onChange={onChangeBuildingBlocks}
        onContinue={onGoToStepsCallbacks[BoundaryGroupFromAIModalSteps.Variables]}
        onSelectSubsetFromMap={onSelectSubsetFromMap}
        buildingBlockSelection={config.buildingBlockSelection}
        buildingBlockSubsetType={config.buildingBlockSubsetType}
        selectedBoundaryIds={config.selectedBoundaryIds}
        onChangeBuildingBlockSelection={onChangeBuildingBlockSelection}
        onChangeBuildingBlockSubsetType={onChangeBuildingBlockSubsetType}
      />
    ),
    isExpanded: isExpandedPerStep[BoundaryGroupFromAIModalSteps.BuildingBlock],
    isLocked: isLockedPerStep[BoundaryGroupFromAIModalSteps.BuildingBlock],
    onHeadingClick: onGoToStepsCallbacks[BoundaryGroupFromAIModalSteps.BuildingBlock],
  }, {
    header: (
      <Accordion.ItemHeading css={itemButtonStyle}>
        <Accordion.StepNumber />
        <span>{t('boundaryAI.Select territory creation variables')}</span>
      </Accordion.ItemHeading>
    ),
    child: (
      <VariablesStepComponent
        applyFilterOnData={config.applyFilterOnData}
        dataMetricsValues={config.dataMetricsValues}
        demographicsValues={config.demographicsValues}
        globalMetrics={config.globalMetrics}
        isLoadingDataMetrics={isLoadingDataMetrics}
        onChangeDataMetrics={onChangeDataMetrics}
        onChangeDemographics={onChangeDemographics}
        onChangeGlobalMetrics={onChangeGlobalMetrics}
        onContinue={onGoToStepsCallbacks[BoundaryGroupFromAIModalSteps.AdditionalOptions]}
        onChangeApplyFilterOnData={showApplyDataFilterOption ? onChangeApplyFilterOnData : undefined}
        minWeight={minWeight}
        maxWeight={maxWeight}
      />
    ),
    isExpanded: isExpandedPerStep[BoundaryGroupFromAIModalSteps.Variables],
    isLocked: isLockedPerStep[BoundaryGroupFromAIModalSteps.Variables],
    onHeadingClick: onGoToStepsCallbacks[BoundaryGroupFromAIModalSteps.Variables],
  }, {
    header: (
      <Accordion.ItemHeading css={itemButtonStyle}>
        <Accordion.StepNumber />
        <span>{t('boundaryAI.Select Territory Creation Method')}</span>
      </Accordion.ItemHeading>
    ),
    child: (
      <AdditionalOptionsStepComponent
        anchorPointsImportance={config.anchorPointsImportance ?? 0}
        onAnchorPointsImportanceChange={onApImportanceChange}
        interlocks={additionalStepInterlocks}
        customBoundaryGroups={customBoundaryGroups}
        dataMetricsValues={config.dataMetricsValues}
        demographicsValues={config.demographicsValues}
        numberOfTerritories={numberOfTerritories}
        numberOfTerritoriesLoading={numberOfTerritoriesLoading}
        numberOfTerritoriesUpdateReason={numberOfTerritoriesUpdateReason}
        onChangeDataMetrics={onChangeDataMetrics}
        onChangeDemographics={onChangeDemographics}
        onChangeNumberOfTerritories={onChangeNumberOfTerritories}
        onChangeOptimizationConstraints={onChangeOptimizationConstraints}
        onChangeOverrideTerritory={onChangeOverrideTerritory}
        onChangePreviouslySavedBoundaryGroupId={onChangePreviouslySavedBoundaryGroupId}
        onChangeRespectPrevTerritories={onChangeRespectPrevTerritories}
        onChangeRespectSalesLocations={onChangeRespectSalesLocations}
        onChangeSalesRepLocationsMap={onChangeSalesRepLocationsMapId}
        onChangeSalesRepNameColumnId={onChangeSalesRepNameColumnId}
        onChangeUseNumberOfTerritories={onChangeUseNumberOfTerritories}
        onContinue={onGoToStepsCallbacks[BoundaryGroupFromAIModalSteps.Name]}
        onRefreshTerritoriesCount={onRefreshTerritoriesCount}
        previouslySavedBoundaryGroupId={config.previousTerritoriesBoundaryGroupId}
        optimizationConstraints={config.optimizationConstraints}
        overrideTerritory={config.overrideTerritory}
        respectPrevTerritories={config.respectPrevTerritories}
        respectSalesLocations={config.respectSalesLocations}
        salesRepLocationsMap={config.salesRepLocationsMap}
        salesRepNameColumnId={config.salesRepNameColumnId}
        showRespectPreviousTerritories={false}
        useNumberOfTerritories={config.useNumberOfTerritories}
        areConstraintsValid={areConstraintsValid}
        isConstraintsValidationLoading={isConstraintsValidationLoading}
        constraintsGeneralError={constraintsGeneralError}
        constraintsValidationData={constraintsValidationData}
      />
    ),
    isExpanded: isExpandedPerStep[BoundaryGroupFromAIModalSteps.AdditionalOptions],
    isLocked: isLockedPerStep[BoundaryGroupFromAIModalSteps.AdditionalOptions],
    onHeadingClick: onGoToStepsCallbacks[BoundaryGroupFromAIModalSteps.AdditionalOptions],
  }, {
    header: (
      <Accordion.ItemHeading css={itemButtonStyle}>
        <Accordion.StepNumber />
        <span>{t('boundaryAI.Name Your territory group')}</span>
      </Accordion.ItemHeading>
    ),
    child: (
      <NameStepComponent
        groupName={config.groupName}
        onChange={onChangeGroupName}
        onContinue={onSubmit}
        isSubmitDisabled={isGenerateOptimizedTerritoriesLoading || (CONFIG.ENABLE_OPTIMIZED_TERRITORY_AUTO_NAME_BUILDER ? false : isTextEmpty(config.groupName))}
        optimizedTerritoriesRequestError={optimizedTerritoriesRequestError}
        onCloseOptimizedTerritoriesRequestError={onCloseOptimizedTerritoriesRequestError}
        options={options}
        onOptionChange={(id, value) => {
          if (id === 'export-config') {
            onChangeExportConfig(value);
          }
        }}
      />
    ),
    isExpanded: isExpandedPerStep[BoundaryGroupFromAIModalSteps.Name],
    isLocked: isLockedPerStep[BoundaryGroupFromAIModalSteps.Name],
    onHeadingClick: onGoToStepsCallbacks[BoundaryGroupFromAIModalSteps.Name],
  },
  ];

  const isLoading = isLoadingSubsetBlocks || isGenerateOptimizedTerritoriesLoading;

  return (
    <>
      <OutlinePanelComponent
        outlines={[OutlinePanelOptions.Top]}
      >
        <Accordion>
          {isLoading && <OverlayLoaderComponent />}
          {accordionData.map((item, index) => (
            <Accordion.Item
              key={index}
              data-testid={item.testId}
              open={item.isExpanded}
              isLocked={item.isLocked}
              onToggle={item.isLocked ? undefined : item.onHeadingClick}
            >
              {item.header}

              <Accordion.Panel>
                {item.child}
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </OutlinePanelComponent>
    </>
  );
};

export type CreateOptimizedTerritoriesModalProps = ModalProps<CreateOptimizedTerritoriesProps>;

export const CreateOptimizedTerritoriesModalComponent: FC<CreateOptimizedTerritoriesModalProps> = ({
  onClose,
  isOpen,
  ...restProps
}: CreateOptimizedTerritoriesModalProps) => {
  const [t] = useTranslation();

  return (
    <ModalComponent
      onClose={onClose}
      isOpen={isOpen}
      // TODO: remove Maptive suffix after development is done
      caption={t('Auto Territory Builder') + ' (Maptive)'}
      contentStyle={css({ padding: 0 })}
      maxWidth="600px"
    >
      <CreateOptimizedTerritoriesComponent {...restProps} />
    </ModalComponent>
  );
};
